import { cloneDeep } from 'lodash-es'

import { GeoreferencedMap } from '@allmaps/annotation'
import { Image } from '@allmaps/iiif-parser'
import {
  ProjectedGcpTransformer,
  ProjectedGcpTransformerOptions,
  Projection,
  webMercatorProjection
} from '@allmaps/project'
import {
  computeBbox,
  bboxToRectangle,
  rectanglesToScale,
  fetchImageInfo,
  getPropertyFromCacheOrComputation,
  getPropertyFromDoubleCacheOrComputation,
  mixLineStrings,
  sizeToRectangle,
  mergeOptions,
  mergeOptionsUnlessUndefined,
  objectDifference,
  omit,
  mergePartialOptions,
  mixNumbers
} from '@allmaps/stdlib'

import { applyHomogeneousTransform } from '../shared/homogeneous-transform.js'
import { WarpedMapEvent, WarpedMapEventType } from '../shared/events.js'

import type {
  Gcp,
  Point,
  Ring,
  Rectangle,
  Bbox,
  TileZoomLevel,
  Size
} from '@allmaps/types'
import type {
  Helmert,
  TransformationType,
  DistortionMeasure
} from '@allmaps/transform'

import type {
  AnimationOptions,
  AnimationInternalOptions,
  GetWarpedMapOptions,
  WarpedMapFactory,
  WarpedMapListOptions,
  WarpedMapOptions,
  ShouldRenderOptions,
  WarpedMapWithoutGeoreferencedMapOptions
} from '../shared/types.js'
import type { Viewport } from '../viewport/Viewport.js'
import type { FetchableTile } from '../tilecache/FetchableTile.js'

export const UNDEFINED_GEOREFERENCED_MAP_OPTIONS = {
  transformationType: undefined,
  internalProjection: undefined
}

export const DEFAULT_WARPED_MAP_OPTIONS: WarpedMapOptions = {
  gcps: [],
  resourceMask: [],
  transformationType: 'polynomial' as TransformationType,
  internalProjection: webMercatorProjection,
  projection: webMercatorProjection,
  visible: true,
  anticipateVisibility: false,
  overviewTilesMaxResolution: undefined, // Used to be 1024 * 1024 to support one such tile, e.g. for Rotterdam map.
  overviewTilesSelection: 'lowest',
  applyMask: true,
  distortionMeasure: undefined
}

const DEFAULT_SPECIFIC_PROJECTED_GCP_TRANSFORMER_OPTIONS = {
  minOffsetRatio: 0.01,
  maxDepth: 5,
  differentHandedness: true
}
const DEFAULT_PROJECTED_GCP_TRANSFORMER_OPTIONS = {
  ...DEFAULT_WARPED_MAP_OPTIONS,
  ...DEFAULT_SPECIFIC_PROJECTED_GCP_TRANSFORMER_OPTIONS
}

export function createWarpedMapFactory<W extends WarpedMap>() {
  return ((
    mapId: string,
    georeferencedMap: GeoreferencedMap,
    listOptions?: Partial<WarpedMapListOptions<W>>,
    mapOptions?: Partial<GetWarpedMapOptions<W>>
  ) =>
    new WarpedMap(
      mapId,
      georeferencedMap,
      listOptions as Partial<WarpedMapListOptions<WarpedMap>>,
      mapOptions as Partial<WarpedMapOptions>
    )) as WarpedMapFactory<W>
}

/**
 * Class for warped maps.
 * This class describes how a georeferenced map is warped using a specific transformation.
 *
 * @param mapId - ID of the map
 * @param georeferencedMap - Georeferenced map used to construct the WarpedMap
 * @param defaultOptions - Default options
 * @param georeferencedMapOptions - Options from georeferenced map
 * @param listOptions - Options from warped map list
 * @param mapOptions - Options specific to this map
 * @param options - Result of merging default, georeferenced map, list and specific map options
 * @param fetchingImageInfo - Whether the image information is loading
 * @param image - Parsed IIIF image
 * @param tileSize - Size of the tiles
 * @param mixed - Whether the options were last set by mixing previous and new properties, i.e. when rerendering during an ongoing animation
 * @param visible - Visibility of the map
 * @param previousVisible - Previous visible
 * @param visibilityOpacity - Opacity linked to visibility of the map
 * @param previousVisibilityOpacity - Opacity linked to previous visibility of the map
 * @param applyMask - Apply the mask
 * @param previousApplyMask - Previous applyMask
 * @param applyMaskOpacity - Opacity for the triangles outside the mask, linked to appling of the mask
 * @param previousApplyMaskOpacity - Opacity for the triangles outside the mask, linked to previous appling of the mask
 * @param gcps - Ground control points used for warping this map, from resource coordinates to geospatial coordinates
 * @param projectedGcps - Projected ground control points, from resource coordinates to projected geospatial coordinates
 * @param resourcePoints - The resource coordinates of the ground control points
 * @param geoPoints - The geospatial coordinates of the ground control points
 * @param projectedGeoPoints - The projected geospatial coordinates of the projected ground control points
 * @param projectedGeoTransformedResourcePoints - The resource coordinates of the ground control points, transformed to projected geospatial coordinates using the projected transformer
 * @param projectedGeoPreviousTransformedResourcePoints - The projectedGeoTransformedResourcePoints of the previous transformation type, used during transformation transitions
 * @param resourceFullMask - Resource full mask (describing the entire extent of the image)
 * @param resourceFullMaskBbox - Bbox of the resource full mask
 * @param resourceFullMaskRectangle - Rectangle of the resource full mask bbox
 * @param resourceAppliedMask - Resource applied mask. In case 'applyMask' is true, this is the resourceMask. In case 'applyMask' is false, this is the resourceFullMask.
 * @param resourceAppliedMaskBbox - Bbox of the resourceAppliedMask
 * @param resourceAppliedMaskRectangle - Rectangle of the resourceAppliedMaskBbox
 * @param resourceMask - Resource mask
 * @param resourceMaskBbox - Bbox of the resourceMask
 * @param resourceMaskRectangle - Rectangle of the resourceMaskBbox
 * @param transformationType - Transformation type used in the transfomer. This is loaded from the georeference annotation.
 * @param previousTransformationType - Previous transformation type
 * @param internalProjection - Internal projection used in the projected transformer
 * @param previousInternalProjection - Previous internal projection
 * @param projection - Projection of the projected geospatial coordinates space
 * @param projectedTransformer - Transformer used for warping this map from resource coordinates to projected geospatial coordinates
 * @param projectedPreviousTransformer - Previous transformer used for warping this map from resource coordinates to projected geospatial coordinates
 * @param geoFullMask - resourceMask in geospatial coordinates
 * @param geoFullMaskBbox - Bbox of the geoFullMask
 * @param geoFullMaskRectangle - resourceFullMaskRectangle in geospatial coordinates
 * @param geoMask - resourceMask in geospatial coordinates
 * @param geoMaskBbox - Bbox of the geoMask
 * @param geoMaskRectangle - resourceMaskRectangle in geospatial coordinates
 * @param geoAppliedMask - resourceAppliedMask in geospatial coordinates
 * @param geoAppliedMaskBbox - Bbox of the geoFullMask
 * @param geoAppliedMaskRectangle - resourceAppliedMaskRectangle in geospatial coordinates
 * @param projectedGeoFullMask - resourceFullMask in projected geospatial coordinates
 * @param projectedGeoFullMaskBbox - Bbox of the projectedGeoFullMask
 * @param projectedGeoFullMaskRectangle - resourceFullMaskRectangle in projected geospatial coordinates
 * @param projectedGeoMask - resourceMask in projected geospatial coordinates
 * @param projectedGeoMaskBbox - Bbox of the projectedGeoMask
 * @param projectedGeoMaskRectangle - resourceMaskRectanglee in projected geospatial coordinates
 * @param projectedGeoAppliedMask - resourceAppliedMask in projected geospatial coordinates
 * @param projectedGeoAppliedMaskBbox - Bbox of the projectedGeoAppliedMask
 * @param projectedGeoAppliedMaskRectangle - resourceAppliedMaskRectangle in projected geospatial coordinates
 * @param resourceToProjectedGeoScale - Scale of the warped map, in resource pixels per projected geospatial coordinates
 * @param distortionMeasure - Distortion measure displayed for this map
 * @param previousDistortionMeasure - Previous distortion measure displayed for this map
 * @param tileZoomLevelForViewport - The tile zoom level, for the current viewport
 * @param overviewTileZoomLevelForViewport - The overview tile zoom level, for the current viewport
 * @param projectedGeoBufferedViewportRectangleForViewport - The (buffered) viewport in projected geospatial coordinates, for the current viewport
 * @param projectedGeoBufferedViewportRectangleBboxForViewport - Bbox of the projectedGeoBufferedViewportRectangle
 * @param resourceBufferedViewportRingForViewport - The (buffered) viewport transformed back to resource coordinates, for the current viewport
 * @param resourceBufferedViewportRingBboxForViewport - Bbox of the resourceViewportRing
 * @param resourceBufferedViewportRingBboxAndResourceMaskBboxIntersectionForViewport - The intersection of the bbox of the (buffered) viewport transformed back to resource coordinates and the bbox of the resource mask, for the current viewport
 * @param fetchableTilesForViewport - The fetchable tiles for displaying this map, for the current viewport
 * @param overviewFetchableTilesForViewport - The overview fetchable tiles, for the current viewport
 */
export class WarpedMap extends EventTarget {
  mapId: string
  georeferencedMap: GeoreferencedMap

  defaultOptions!: WarpedMapOptions
  georeferencedMapOptions: Partial<WarpedMapOptions>
  listOptions: Partial<WarpedMapListOptions<WarpedMap>>
  mapOptions: Partial<WarpedMapOptions>
  options!: WarpedMapOptions

  fetchingImageInfo: boolean
  image?: Image
  tileSize?: Size

  protected abortController?: AbortController

  mixed = false

  visible!: boolean
  previousVisible!: boolean
  visibilityOpacity!: number
  previousVisibilityOpacity!: number
  applyMask!: boolean
  previousApplyMask!: boolean
  applyMaskOpacity!: number
  previousApplyMaskOpacity!: number

  gcps!: Gcp[]
  projectedGcps!: Gcp[]
  resourcePoints!: Point[]
  geoPoints!: Point[]
  projectedGeoPoints!: Point[]
  projectedGeoTransformedResourcePoints!: Point[]
  projectedGeoPreviousTransformedResourcePoints!: Point[]

  resourceFullMask!: Ring
  resourceFullMaskBbox!: Bbox
  resourceFullMaskRectangle!: Rectangle
  resourceMask!: Ring
  resourceMaskBbox!: Bbox
  resourceMaskRectangle!: Rectangle
  resourceAppliedMask!: Ring
  resourceAppliedMaskBbox!: Bbox
  resourceAppliedMaskRectangle!: Rectangle

  transformationType!: TransformationType
  previousTransformationType!: TransformationType

  internalProjection!: Projection
  previousInternalProjection!: Projection
  projection!: Projection

  projectedTransformer!: ProjectedGcpTransformer
  projectedPreviousTransformer!: ProjectedGcpTransformer
  protected projectedTransformerCache: Map<
    TransformationType,
    ProjectedGcpTransformer
  >
  protected projectedTransformerDoubleCache: Map<
    TransformationType,
    Map<string, ProjectedGcpTransformer>
  >

  geoFullMask!: Ring
  geoFullMaskBbox!: Bbox
  geoFullMaskRectangle!: Rectangle
  geoMask!: Ring
  geoMaskBbox!: Bbox
  geoMaskRectangle!: Rectangle
  geoAppliedMask!: Ring
  geoAppliedMaskBbox!: Bbox
  geoAppliedMaskRectangle!: Rectangle

  projectedGeoFullMask!: Ring
  projectedGeoFullMaskBbox!: Bbox
  projectedGeoFullMaskRectangle!: Rectangle
  projectedGeoMask!: Ring
  projectedGeoMaskBbox!: Bbox
  projectedGeoMaskRectangle!: Rectangle
  projectedGeoAppliedMask!: Ring
  projectedGeoAppliedMaskBbox!: Bbox
  projectedGeoAppliedMaskRectangle!: Rectangle

  resourceToProjectedGeoScale!: number

  distortionMeasure?: DistortionMeasure
  previousDistortionMeasure?: DistortionMeasure

  tileZoomLevelForViewport?: TileZoomLevel
  overviewTileZoomLevelForViewport?: TileZoomLevel

  projectedGeoBufferedViewportRectangleForViewport?: Rectangle
  projectedGeoBufferedViewportRectangleBboxForViewport?: Bbox

  resourceBufferedViewportRingForViewport?: Ring
  resourceBufferedViewportRingBboxForViewport?: Bbox

  resourceBufferedViewportRingBboxAndResourceMaskBboxIntersectionForViewport?: Bbox

  fetchableTilesForViewport: FetchableTile[] = []
  overviewFetchableTilesForViewport: FetchableTile[] = []

  /**
   * Creates an instance of WarpedMap.
   *
   * @param mapId - ID of the map
   * @param georeferencedMap - Georeferenced map used to construct the WarpedMap
   * @param options - options
   */
  constructor(
    mapId: string,
    georeferencedMap: GeoreferencedMap,
    listOptions: Partial<WarpedMapListOptions<WarpedMap>> = {},
    mapOptions: Partial<WarpedMapOptions> = {}
  ) {
    super()

    // Note: defaults are overwritten by georeferenced map input (only if they are defined),
    // which is overwritten by list options and options
    // This way a warped map list's current transformation and projection,
    // passed as option, can overwrite those of the georeferenced map (i.e. annotation)

    this.mapId = mapId
    this.georeferencedMap = georeferencedMap

    this.projectedTransformerCache = new Map()
    this.projectedTransformerDoubleCache = new Map()
    this.fetchingImageInfo = false

    this.mapOptions = mapOptions
    this.listOptions = listOptions
    this.georeferencedMapOptions = {
      transformationType: georeferencedMap.transformation
        ?.type as TransformationType,
      internalProjection: georeferencedMap.resourceCrs as Projection,
      gcps: georeferencedMap.gcps,
      resourceMask: georeferencedMap.resourceMask
    }
    this.setDefaultOptions()
    this.applyOptions({ stage: 'init' })
  }

  /**
   * Get default options
   */
  static getDefaultOptions(): WarpedMapOptions {
    return DEFAULT_WARPED_MAP_OPTIONS
  }

  /**
   * Get default options without the options overwritten by the georeferenced map
   */
  static getDefaultWithoutGeoreferencedMapOptions(): WarpedMapWithoutGeoreferencedMapOptions {
    return omit(DEFAULT_WARPED_MAP_OPTIONS, [
      'transformationType',
      'internalProjection',
      'resourceMask',
      'gcps'
    ])
  }

  /**
   * Get the resource applied mask, mask or full mask, based on an input option.
   */
  getResourceAppliedMask(applyMask?: boolean): Ring {
    if (applyMask === undefined) {
      return this.resourceAppliedMask
    } else if (applyMask) {
      return this.resourceMask
    } else {
      return this.resourceFullMask
    }
  }

  /**
   * Get the geo applied mask, mask or full mask, based on an input option.
   */
  getGeoAppliedMask(applyMask?: boolean): Ring {
    if (applyMask === undefined) {
      return this.geoAppliedMask
    } else if (applyMask) {
      return this.geoMask
    } else {
      return this.geoFullMask
    }
  }

  /**
   * Get the projected geo applied mask, mask or full mask, based on an input option.
   */
  getProjectedGeoAppliedMask(applyMask?: boolean): Ring {
    if (applyMask === undefined) {
      return this.projectedGeoAppliedMask
    } else if (applyMask) {
      return this.projectedGeoMask
    } else {
      return this.projectedGeoFullMask
    }
  }

  /**
   * Get default and georeferenced map options
   */
  getDefaultAndGeoreferencedMapOptions(): WarpedMapOptions {
    return mergeOptionsUnlessUndefined(
      this.defaultOptions,
      this.georeferencedMapOptions
    )
  }

  /**
   * Get scale of the warped map, in resource pixels per viewport pixels.
   *
   * @param viewport - the current viewport
   * @returns
   */
  getResourceToViewportScale(viewport: Viewport): number {
    return rectanglesToScale(
      this.resourceMaskRectangle,
      this.projectedGeoMaskRectangle.map((point) => {
        return applyHomogeneousTransform(
          viewport.projectedGeoToViewportHomogeneousTransform,
          point
        )
      }) as Rectangle
    )
  }

  /**
   * Get scale of the warped map, in resource pixels per canvas pixels.
   *
   * @param viewport - the current viewport
   * @returns
   */
  getResourceToCanvasScale(viewport: Viewport): number {
    return this.getResourceToViewportScale(viewport) / viewport.devicePixelRatio
  }

  /**
   * Get the reference scaling from the forward transformation of the projected Helmert transformer
   *
   * @returns
   */
  getReferenceScale(): number {
    const projectedHelmertTransformer = this.getProjectedTransformer('helmert')
    const toProjectedGeoHelmertTransformation =
      projectedHelmertTransformer.getToGeoTransformation() as Helmert
    const helmertMeasures = toProjectedGeoHelmertTransformation.getMeasures()
    return helmertMeasures.scale as number
  }

  /**
   * Get a projected transformer of the given transformation type.
   *
   * Uses cashed projected transformers by transformation type,
   * and only computes a new projected transformer if none found.
   *
   * Returns a projected transformer in the current projection,
   * even if the cached transformer was computed in a different projection.
   *
   * Default settings apply for the options.
   *
   * @params transformationType - the transformation type
   * @params partialProjectedGcpTransformerOptions - options
   * @returns A projected transformer
   */
  getProjectedTransformer(
    transformationType?: TransformationType,
    partialProjectedGcpTransformerOptions?: Partial<ProjectedGcpTransformerOptions>
  ): ProjectedGcpTransformer {
    const options = mergeOptionsUnlessUndefined(
      DEFAULT_PROJECTED_GCP_TRANSFORMER_OPTIONS,
      {
        projection: this.projection,
        internalProjection: this.internalProjection
      },
      partialProjectedGcpTransformerOptions
    )
    transformationType = transformationType ?? this.transformationType

    return getPropertyFromDoubleCacheOrComputation(
      this.projectedTransformerDoubleCache,
      transformationType,
      this.projection.definition,
      () => {
        const projectedTransformer = getPropertyFromCacheOrComputation(
          this.projectedTransformerCache,
          transformationType,
          () =>
            new ProjectedGcpTransformer(
              this.gcps,
              transformationType,
              omit(options, ['projection'])
            )
        )
        return ProjectedGcpTransformer.setProjection(
          cloneDeep(projectedTransformer),
          options.projection
        )
      }
    )
  }

  /**
   * Set the defaultOptions
   */
  setDefaultOptions() {
    this.defaultOptions = WarpedMap.getDefaultOptions()
  }

  /**
   * Set the list options
   *
   * @param listOptions - list options
   * @param animationOptions - Animation options
   */
  setListOptions(
    listOptions?: Partial<WarpedMapOptions>,
    animationOptions?: Partial<AnimationOptions>
  ): object {
    return this.setMapAndListOptions(undefined, listOptions, animationOptions)
  }

  /**
   * Set the map-specific options
   *
   * @param mapOptions - Map-specific options
   * @param animationOptions - Animation options
   */
  setMapOptions(
    mapOptions?: Partial<WarpedMapOptions>,
    animationOptions?: Partial<AnimationOptions & AnimationInternalOptions>
  ): object {
    return this.setMapAndListOptions(mapOptions, undefined, animationOptions)
  }

  /**
   * Set the map-specific options, and the list options
   *
   * @param mapOptions - Map-specific options
   * @param listOptions - list options
   * @param animationOptions - Animation options
   */
  setMapAndListOptions(
    mapOptions?: Partial<WarpedMapOptions>,
    listOptions?: Partial<WarpedMapOptions>,
    animationOptions?: Partial<AnimationOptions & AnimationInternalOptions>
  ): object {
    const optionKeysPossiblyChanged: string[] = []
    if (mapOptions !== undefined && Object.keys(mapOptions).length > 0) {
      this.mapOptions = mergeOptions(this.mapOptions, mapOptions)
      optionKeysPossiblyChanged.push(...Object.keys(mapOptions))
    }
    if (listOptions !== undefined && Object.keys(listOptions).length > 0) {
      this.listOptions = mergeOptions(this.listOptions, listOptions)
      optionKeysPossiblyChanged.push(...Object.keys(listOptions))
    }
    return this.applyOptions(
      mergePartialOptions(animationOptions, { optionKeysPossiblyChanged })
    )
  }

  protected applyOptions(
    animationOptions?: Partial<AnimationOptions & AnimationInternalOptions>
  ): object {
    const options = mergeOptionsUnlessUndefined(
      this.defaultOptions,
      this.georeferencedMapOptions,
      this.listOptions,
      this.mapOptions
    )

    // If some options should be omitted from changing,
    // like when setting all options exept those that should be animated,
    // then omit those options and set the options accordingly
    const optionKeysToConsider =
      animationOptions?.optionKeysPossiblyChanged?.filter((k) =>
        animationOptions.optionKeysToOmit
          ? !animationOptions.optionKeysToOmit.includes(k)
          : true
      )
    const changedOptions = objectDifference(
      options,
      this.options,
      optionKeysToConsider
    )
    this.options = mergeOptions(this.options, changedOptions)

    if (animationOptions?.stage == 'init') {
      // On init we should set the properties in a specific order
      // and update the projected transformer properties only once at the end

      this.visible = this.options.visible
      this.previousVisible = this.visible
      this.visibilityOpacity = this.visible ? 1 : 0
      this.previousVisibilityOpacity = this.visibilityOpacity
      this.applyMask = this.options.applyMask
      this.previousApplyMask = this.applyMask
      this.applyMaskOpacity = this.applyMask ? 0 : 1
      this.previousApplyMaskOpacity = this.applyMaskOpacity

      this.gcps = this.options.gcps

      this.resourceFullMask = this.getResourceFullMask()
      this.resourceMask = this.georeferencedMap.resourceMask
      this.resourceAppliedMask = this.applyMask
        ? this.resourceMask
        : this.resourceFullMask
      this.updateResourceMaskProperties()

      this.transformationType = this.options.transformationType
      this.previousTransformationType = this.transformationType

      this.internalProjection = this.options.internalProjection
      this.previousInternalProjection = this.internalProjection
      this.projection = this.options.projection

      this.updateProjectedTransformerProperties()
    } else {
      if ('visible' in changedOptions) {
        this.visible = this.options.visible
        this.visibilityOpacity = this.visible ? 1 : 0
      }

      if ('gcps' in changedOptions) {
        this.setGcps(this.options.gcps)
      }

      if ('resourceMask' in changedOptions || 'applyMask' in changedOptions) {
        this.applyMask = this.options.applyMask
        this.applyMaskOpacity = this.applyMask ? 0 : 1
        this.setResourceMask()
      }

      if ('transformationType' in changedOptions) {
        this.setTransformationType(this.options.transformationType)
      }

      if ('internalProjection' in changedOptions) {
        this.setInternalProjection(this.options.internalProjection)
      }

      if ('projection' in changedOptions) {
        this.setProjection(this.options.projection)
      }

      if (
        'distortionMeasure' in changedOptions ||
        'distortionMeasures' in changedOptions
      ) {
        this.setDistortionMeasure(this.options.distortionMeasure)
      }
    }

    return changedOptions
  }

  shouldRenderMap(_partialOptions?: Partial<ShouldRenderOptions>): boolean {
    // this.options.visible is equal to this.visible
    return this.options.visible !== false || this.previousVisible !== false
  }

  shouldRenderLines(): boolean {
    return this.options.visible !== false || this.previousVisible !== false
  }

  shouldRenderPoints(): boolean {
    return this.options.visible !== false || this.previousVisible !== false
  }

  /**
   * Update the ground control points loaded from a georeferenced map to new ground control points.
   *
   * @param gcps
   */
  protected setGcps(gcps: Gcp[]): void {
    this.gcps = gcps
    this.clearProjectedTransformerCaches()
    this.updateProjectedTransformerProperties()
  }

  /**
   * Update the resource mask loaded from a georeferenced map to a new mask.
   *
   * @param resourceMask
   */
  protected setResourceMask(): void {
    this.resourceFullMask = this.getResourceFullMask()
    this.resourceMask = this.options.resourceMask
    this.resourceAppliedMask = this.applyMask
      ? this.resourceMask
      : this.resourceFullMask
    this.updateResourceMaskProperties()
    this.updateGeoMaskProperties()
    this.updateProjectedGeoMaskProperties()
  }

  /**
   * Set the transformationType
   *
   * @param transformationType
   */
  protected setTransformationType(
    transformationType: TransformationType
  ): void {
    this.transformationType = transformationType
    if (!this.previousTransformationType) {
      this.previousTransformationType = this.transformationType
    }
    this.updateProjectedTransformerProperties()
  }

  /**
   * Set the distortionMeasure
   *
   * @param distortionMeasure - the disortion measure
   */
  protected setDistortionMeasure(distortionMeasure?: DistortionMeasure): void {
    this.distortionMeasure = distortionMeasure
  }

  /**
   * Set the internal projection
   *
   * @param projection - the internal projection
   */
  protected setInternalProjection(projection?: Projection): void {
    this.internalProjection =
      projection ||
      DEFAULT_PROJECTED_GCP_TRANSFORMER_OPTIONS.internalProjection ||
      webMercatorProjection
    if (!this.previousInternalProjection) {
      this.previousInternalProjection = this.internalProjection
    }
    this.clearProjectedTransformerCaches()
    // Note: the following will recreate a transformer with the internal projection
    // and also assure the triangulation is updated.
    this.updateProjectedTransformerProperties()
  }

  /**
   * Set the projection
   *
   * @param projection - the projection
   */
  protected setProjection(projection?: Projection): void {
    this.projection =
      projection ||
      DEFAULT_PROJECTED_GCP_TRANSFORMER_OPTIONS.projection ||
      webMercatorProjection
    // Note: the following will reuse the existing transformer but set it's projection
    // and also assure the triangulation is updated.
    this.updateProjectedTransformerProperties()
  }

  /**
   * Set the tile zoom level for the current viewport
   *
   * @param tileZoomLevel - tile zoom level for the current viewport
   */
  setTileZoomLevelForViewport(tileZoomLevel?: TileZoomLevel) {
    this.tileZoomLevelForViewport = tileZoomLevel
  }

  /**
   * Set the overview tile zoom level for the current viewport
   *
   * @param tileZoomLevel - tile zoom level for the current viewport
   */
  setOverviewTileZoomLevelForViewport(tileZoomLevel?: TileZoomLevel) {
    this.overviewTileZoomLevelForViewport = tileZoomLevel
  }

  /**
   * Set projectedGeoBufferedViewportRectangle for the current viewport
   *
   * @param projectedGeoBufferedViewportRectangle
   */
  setProjectedGeoBufferedViewportRectangleForViewport(
    projectedGeoBufferedViewportRectangle?: Rectangle
  ) {
    this.projectedGeoBufferedViewportRectangleForViewport =
      projectedGeoBufferedViewportRectangle
    this.projectedGeoBufferedViewportRectangleBboxForViewport =
      projectedGeoBufferedViewportRectangle
        ? computeBbox(projectedGeoBufferedViewportRectangle)
        : undefined
  }

  /**
   * Set resourceBufferedViewportRing for the current viewport
   *
   * @param resourceBufferedViewportRing
   */
  setResourceBufferedViewportRingForViewport(
    resourceBufferedViewportRing?: Ring
  ) {
    this.resourceBufferedViewportRingForViewport = resourceBufferedViewportRing
    this.resourceBufferedViewportRingBboxForViewport =
      resourceBufferedViewportRing
        ? computeBbox(resourceBufferedViewportRing)
        : undefined
  }

  /**
   * Set resourceBufferedViewportRingBboxAndResourceAppliedMaskBboxIntersection for the current viewport
   *
   * @param resourceBufferedViewportRingBboxAndResourceAppliedMaskBboxIntersection
   */
  setResourceBufferedViewportRingBboxAndResourceAppliedMaskBboxIntersectionForViewport(
    resourceBufferedViewportRingBboxAndResourceAppliedMaskBboxIntersection?: Bbox
  ) {
    this.resourceBufferedViewportRingBboxAndResourceMaskBboxIntersectionForViewport =
      resourceBufferedViewportRingBboxAndResourceAppliedMaskBboxIntersection
  }

  /**
   * Set tiles for the current viewport
   *
   * @param fetchableTiles
   */
  setFetchableTilesForViewport(fetchableTiles: FetchableTile[]) {
    this.fetchableTilesForViewport = fetchableTiles
  }

  /**
   * Set overview tiles for the current viewport
   *
   * @param overviewFetchableTiles
   */
  setOverviewFetchableTilesForViewport(
    overviewFetchableTiles: FetchableTile[]
  ) {
    this.overviewFetchableTilesForViewport = overviewFetchableTiles
  }

  /**
   * Reset the properties for the current values
   */
  resetForViewport() {
    this.setTileZoomLevelForViewport()
    this.setOverviewTileZoomLevelForViewport()
    this.setProjectedGeoBufferedViewportRectangleForViewport()
    this.setResourceBufferedViewportRingForViewport()
    this.setFetchableTilesForViewport([])
    this.setOverviewFetchableTilesForViewport([])
  }

  /**
   * Reset previous transform properties to new ones (when completing an animation).
   */
  resetPrevious() {
    this.mixed = false
    this.previousVisible = this.visible
    this.previousVisibilityOpacity = this.visibilityOpacity
    this.previousApplyMask = this.applyMask
    this.previousApplyMaskOpacity = this.applyMaskOpacity
    this.previousTransformationType = this.transformationType
    this.previousDistortionMeasure = this.distortionMeasure
    this.previousInternalProjection = this.internalProjection
    this.projectedPreviousTransformer = cloneDeep(this.projectedTransformer)
    this.projectedGeoPreviousTransformedResourcePoints =
      this.projectedGeoTransformedResourcePoints
  }

  /**
   * Mix previous transform properties with new ones (when changing an ongoing animation).
   *
   * @param t - animation progress
   */
  mixPreviousAndNew(t: number) {
    this.mixed = true
    this.previousVisible = this.visible
    this.previousVisibilityOpacity = mixNumbers(
      this.visibilityOpacity,
      this.previousVisibilityOpacity,
      t
    )
    this.previousApplyMask = this.applyMask
    this.previousApplyMaskOpacity = mixNumbers(
      this.applyMaskOpacity,
      this.previousApplyMaskOpacity,
      t
    )
    this.previousTransformationType = this.transformationType
    this.previousDistortionMeasure = this.distortionMeasure
    this.previousInternalProjection = this.internalProjection
    this.projectedPreviousTransformer = cloneDeep(this.projectedTransformer)
    this.projectedGeoPreviousTransformedResourcePoints = mixLineStrings(
      this.projectedGeoTransformedResourcePoints,
      this.projectedGeoPreviousTransformedResourcePoints,
      t
    )
  }

  /**
   * Check if this instance has parsed image
   *
   * @returns
   */
  hasImage(): this is WarpedMapWithImage {
    return this.image !== undefined
  }

  /**
   * Load the parsed image from cache, or fetch and parse the image info to create it
   *
   * @returns
   */
  async loadImage(imagesById?: Map<string, Image>): Promise<void> {
    try {
      const resourceId = this.georeferencedMap.resource.id

      if (imagesById && imagesById.has(resourceId)) {
        this.image = imagesById.get(resourceId)!
      } else {
        this.fetchingImageInfo = true
        this.abortController = new AbortController()
        const signal = this.abortController.signal
        const imageInfo = await fetchImageInfo(
          resourceId,
          { signal },
          this.options.fetchFn
        )
        this.abortController = undefined
        this.image = Image.parse(imageInfo)
        if (imagesById) {
          imagesById.set(resourceId, this.image)
        }
      }
      this.tileSize = [
        Math.max(...this.image.tileZoomLevels.map((size) => size.width)),
        Math.max(...this.image.tileZoomLevels.map((size) => size.height))
      ]

      this.dispatchEvent(new WarpedMapEvent(WarpedMapEventType.IMAGELOADED))
    } catch (err) {
      this.fetchingImageInfo = false
      throw err
    } finally {
      this.fetchingImageInfo = false
    }
  }

  private updateResourceMaskProperties() {
    this.resourceFullMaskBbox = computeBbox(this.resourceFullMask)
    this.resourceFullMaskRectangle = bboxToRectangle(this.resourceFullMaskBbox)
    this.resourceMaskBbox = computeBbox(this.resourceMask)
    this.resourceMaskRectangle = bboxToRectangle(this.resourceMaskBbox)
    this.resourceAppliedMaskBbox = computeBbox(this.resourceAppliedMask)
    this.resourceAppliedMaskRectangle = bboxToRectangle(
      this.resourceAppliedMaskBbox
    )
  }

  private getResourceFullMask() {
    const resourceWidth = this.georeferencedMap.resource.width
    const resourceHeight = this.georeferencedMap.resource.height

    // Per the georeference annotation spec,
    // width and height SHOULD be set on georeferenced map but are not guaranteed,
    // if not set, get (approximate) width and height from resource mask (which MUST be set).
    // TODO: consider to get from image when fetched.
    if (resourceWidth && resourceHeight) {
      return sizeToRectangle([resourceWidth, resourceHeight])
    } else {
      return bboxToRectangle(computeBbox(this.georeferencedMap.resourceMask))
    }
  }

  private updateGeoMaskProperties() {
    this.updateFullGeoMask()
    this.updateGeoMask()
    this.updateAppliedGeoMask()
  }

  private updateProjectedGeoMaskProperties() {
    this.updateProjectedFullGeoMask()
    this.updateProjectedGeoMask()
    this.updateProjectedAppliedGeoMask()
    this.updateResourceToProjectedGeoScale()
  }

  protected updateProjectedTransformerProperties(): void {
    this.updateProjectedTransformer()

    this.updateGeoMaskProperties()
    this.updateProjectedGeoMaskProperties()
    this.updateGcpsProperties()
  }

  private updateProjectedTransformer(): void {
    this.projectedTransformer = this.getProjectedTransformer(
      this.transformationType
    )
    if (!this.projectedPreviousTransformer) {
      this.projectedPreviousTransformer = this.projectedTransformer
    }
  }

  private updateFullGeoMask(): void {
    this.geoFullMask = this.projectedTransformer.transformToGeo([
      this.resourceFullMask
    ])[0]
    this.geoFullMaskBbox = computeBbox(this.geoFullMask)
    this.geoFullMaskRectangle = this.projectedTransformer.transformToGeo(
      [this.resourceFullMaskRectangle],
      { maxDepth: 0 }
    )[0] as Rectangle
  }

  private updateGeoMask(): void {
    this.geoMask = this.projectedTransformer.transformToGeo([
      this.resourceMask
    ])[0]
    this.geoMaskBbox = computeBbox(this.geoMask)
    this.geoMaskRectangle = this.projectedTransformer.transformToGeo(
      [this.resourceMaskRectangle],
      { maxDepth: 0 }
    )[0] as Rectangle
  }

  private updateAppliedGeoMask(): void {
    this.geoAppliedMask = this.applyMask ? this.geoMask : this.geoFullMask
    this.geoAppliedMaskBbox = this.applyMask
      ? this.geoMaskBbox
      : this.geoFullMaskBbox
    this.geoAppliedMaskRectangle = this.applyMask
      ? this.geoMaskRectangle
      : this.geoFullMaskRectangle
  }

  private updateProjectedFullGeoMask(): void {
    this.projectedGeoFullMask =
      this.projectedTransformer.transformToProjectedGeo([
        this.resourceFullMask
      ])[0]
    this.projectedGeoFullMaskBbox = computeBbox(this.projectedGeoFullMask)
    this.projectedGeoFullMaskRectangle =
      this.projectedTransformer.transformToProjectedGeo(
        [this.resourceFullMaskRectangle],
        { maxDepth: 0 }
      )[0] as Rectangle
  }

  private updateProjectedGeoMask(): void {
    this.projectedGeoMask = this.projectedTransformer.transformToProjectedGeo([
      this.resourceMask
    ])[0]
    this.projectedGeoMaskBbox = computeBbox(this.projectedGeoMask)
    this.projectedGeoMaskRectangle =
      this.projectedTransformer.transformToProjectedGeo(
        [this.resourceMaskRectangle],
        { maxDepth: 0 }
      )[0] as Rectangle
  }

  private updateProjectedAppliedGeoMask(): void {
    this.projectedGeoAppliedMask = this.applyMask
      ? this.projectedGeoMask
      : this.projectedGeoFullMask
    this.projectedGeoAppliedMaskBbox = this.applyMask
      ? this.projectedGeoMaskBbox
      : this.projectedGeoFullMaskBbox
    this.projectedGeoAppliedMaskRectangle = this.applyMask
      ? this.projectedGeoMaskRectangle
      : this.projectedGeoFullMaskRectangle
  }

  private updateResourceToProjectedGeoScale(): void {
    this.resourceToProjectedGeoScale = rectanglesToScale(
      this.resourceMaskRectangle,
      this.projectedGeoMaskRectangle
    )
  }

  private updateGcpsProperties() {
    this.projectedGcps = this.gcps.map(({ resource, geo }) => ({
      resource,
      geo: this.projectedTransformer.lonLatToProjection(geo)
    }))
    this.resourcePoints = this.gcps.map((gcp) => gcp.resource)
    this.geoPoints = this.gcps.map((gcp) => gcp.geo)
    this.projectedGeoPoints = this.projectedGcps.map(
      (projectedGcp) => projectedGcp.geo
    )

    this.projectedGeoTransformedResourcePoints = this.gcps.map((projectedGcp) =>
      this.projectedTransformer.transformToProjectedGeo(projectedGcp.resource)
    )

    if (!this.projectedGeoPreviousTransformedResourcePoints) {
      this.projectedGeoPreviousTransformedResourcePoints =
        this.projectedGeoTransformedResourcePoints
    }
  }

  protected clearProjectedTransformerCaches() {
    this.projectedTransformerCache = new Map()
    this.projectedTransformerDoubleCache = new Map()
  }

  destroy() {
    if (this.abortController) {
      this.abortController.abort()
    }
  }
}

/**
 * Class for warped maps with image ID and parsed IIIF image.
 */
export class WarpedMapWithImage extends WarpedMap {
  declare imageId: string
  declare image: Image
  declare tileSize: Size

  constructor(
    mapId: string,
    georeferencedMap: GeoreferencedMap,
    options?: Partial<WarpedMapOptions>
  ) {
    super(mapId, georeferencedMap, options)
  }
}
